import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useNotificationStore } from '@store/notificationStore';
import { getSsoSettingsErrorMessage } from '@utils/ssoErrorMessages';
import {
  useDisconnectGoogleSso,
  useDisconnectMicrosoftSso,
  useGoogleAllowedUsers,
  useGoogleDirectoryUsers,
  useGoogleSsoStatus,
  useMicrosoftAllowedUsers,
  useMicrosoftDirectoryUsers,
  useMicrosoftSsoStatus,
  useRevokeGoogleAllowedUser,
  useRevokeMicrosoftAllowedUser,
  useSetGoogleAllowedUsers,
  useSetMicrosoftAllowedUsers,
  useStartGoogleSsoConnect,
  useStartMicrosoftSsoConnect,
} from '@api/hooks/useSso';
import { SsoProviderCard } from '@components/Admin/SsoProviderCard';

const DIRECTORY_SEARCH_DEBOUNCE_MS = 350;

// Local debounce helper: no debounce utility exists elsewhere in this
// codebase, and each directory search triggers a live app-only-token +
// directory-listing round-trip server-side, so firing on every keystroke
// would be wasteful.
const useDebouncedValue = <T,>(value: T, delayMs: number): T => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
};

const CONNECTED_LABELS: Record<string, string> = {
  microsoft: 'Microsoft 365 SSO connected.',
  google: 'Google Workspace SSO connected.',
};

export const SsoSettings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { addNotification } = useNotificationStore();

  // The admin-consent callback redirects back to `/settings/sso?error=...`
  // (translated by SsoSettingsRedirect in App.tsx to `/admin/settings?tab=sso&error=...`)
  // or `?connected=microsoft`/`?connected=google` on success — surface both via
  // the same toast mechanism used elsewhere on this page (e.g. connect/disconnect
  // mutations).
  useEffect(() => {
    const consentError = searchParams.get('error');
    const connected = searchParams.get('connected');

    if (consentError) {
      addNotification({ message: getSsoSettingsErrorMessage(consentError)!, type: 'error' });
    } else if (connected) {
      addNotification({
        message: CONNECTED_LABELS[connected] ?? 'SSO connected.',
        type: 'success',
      });
    } else {
      return;
    }

    const next = new URLSearchParams(searchParams);
    next.delete('error');
    next.delete('connected');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Microsoft
  const microsoftStatus = useMicrosoftSsoStatus();
  const microsoftConnect = useStartMicrosoftSsoConnect();
  const microsoftDisconnect = useDisconnectMicrosoftSso();
  const [microsoftSearch, setMicrosoftSearch] = useState('');
  const debouncedMicrosoftSearch = useDebouncedValue(microsoftSearch, DIRECTORY_SEARCH_DEBOUNCE_MS);
  const microsoftDirectoryUsers = useMicrosoftDirectoryUsers(
    debouncedMicrosoftSearch || undefined,
    microsoftStatus.data?.status === 'connected'
  );
  const microsoftAllowedUsers = useMicrosoftAllowedUsers();
  const setMicrosoftAllowedUsers = useSetMicrosoftAllowedUsers();
  const revokeMicrosoftAllowedUser = useRevokeMicrosoftAllowedUser();

  // Google
  const googleStatus = useGoogleSsoStatus();
  const googleConnect = useStartGoogleSsoConnect();
  const googleDisconnect = useDisconnectGoogleSso();
  const [googleSearch, setGoogleSearch] = useState('');
  const debouncedGoogleSearch = useDebouncedValue(googleSearch, DIRECTORY_SEARCH_DEBOUNCE_MS);
  const googleDirectoryUsers = useGoogleDirectoryUsers(
    debouncedGoogleSearch || undefined,
    googleStatus.data?.status === 'connected'
  );
  const googleAllowedUsers = useGoogleAllowedUsers();
  const setGoogleAllowedUsers = useSetGoogleAllowedUsers();
  const revokeGoogleAllowedUser = useRevokeGoogleAllowedUser();

  return (
    <div className="space-y-6">
      <SsoProviderCard
        title="Microsoft 365 SSO"
        description="Let users in your organization sign in with their Microsoft 365 account."
        providerLabel="Microsoft 365"
        directoryLabel="Microsoft 365 directory"
        statusQuery={microsoftStatus}
        connectMutation={microsoftConnect}
        disconnectMutation={microsoftDisconnect}
        search={microsoftSearch}
        onSearchChange={setMicrosoftSearch}
        directoryUsersQuery={microsoftDirectoryUsers}
        allowedUsersQuery={microsoftAllowedUsers}
        setAllowedUsersMutation={setMicrosoftAllowedUsers}
        revokeAllowedUserMutation={revokeMicrosoftAllowedUser}
      />

      <SsoProviderCard
        title="Google Workspace SSO"
        description="Let users in your organization sign in with their Google Workspace account."
        providerLabel="Google Workspace"
        directoryLabel="Google Workspace directory"
        statusQuery={googleStatus}
        connectMutation={googleConnect}
        disconnectMutation={googleDisconnect}
        search={googleSearch}
        onSearchChange={setGoogleSearch}
        directoryUsersQuery={googleDirectoryUsers}
        allowedUsersQuery={googleAllowedUsers}
        setAllowedUsersMutation={setGoogleAllowedUsers}
        revokeAllowedUserMutation={revokeGoogleAllowedUser}
      />
    </div>
  );
};
